<script lang="ts">
	import { locale, _ } from 'svelte-i18n';
	import { onMount } from 'svelte';
	
	interface Language {
		code: string;
		name: string;
		flag: string;
	}
	
	const languages: Language[] = [
		{ code: 'en', name: 'English', flag: '🇬🇧' },
		{ code: 'sv-SE', name: 'Svenska', flag: '🇸🇪' },
		{ code: 'fr-FR', name: 'Français', flag: '🇫🇷' },
		{ code: 'es-ES', name: 'Español', flag: '🇪🇸' },
		{ code: 'de-DE', name: 'Deutsch', flag: '🇩🇪' },
		{ code: 'ja-JP', name: '日本語', flag: '🇯🇵' },
		{ code: 'zh-CN', name: '中文', flag: '🇨🇳' },
		{ code: 'it-IT', name: 'Italiano', flag: '🇮🇹' },
		{ code: 'ko-KR', name: '한국어', flag: '🇰🇷' },
		{ code: 'pt-BR', name: 'Português', flag: '🇧🇷' },
		{ code: 'vi-VN', name: 'Tiếng Việt', flag: '🇻🇳' },
		{ code: 'pl-PL', name: 'Polski', flag: '🇵🇱' },
		{ code: 'nl-NL', name: 'Nederlands', flag: '🇳🇱' },
		{ code: 'nb-NO', name: 'Norsk', flag: '🇳🇴' },
		{ code: 'da-DK', name: 'Dansk', flag: '🇩🇰' },
		{ code: 'fi-FI', name: 'Suomi', flag: '🇫🇮' },
		{ code: 'uk-UA', name: 'Українська', flag: '🇺🇦' }
	];
	
	let isOpen = $state(false);
	
	// Get initial locale synchronously from localStorage to prevent flash
	function getStoredLocale(): string {
		if (typeof window !== 'undefined') {
			return localStorage.getItem('locale') || $locale || 'en';
		}
		return $locale || 'en';
	}
	
	let currentLocale = $state(getStoredLocale());
	
	$effect(() => {
		// Update when locale changes from other sources
		if ($locale) {
			currentLocale = $locale;
		}
	});
	
	const currentLanguage = $derived(
		languages.find(lang => lang.code === currentLocale) || languages[0]
	);
	
	function toggleDropdown() {
		isOpen = !isOpen;
	}
	
	function selectLanguage(code: string) {
		locale.set(code);
		localStorage.setItem('locale', code);
		// Also set a cookie for SSR
		document.cookie = `locale=${code};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
		// Update the html lang attribute immediately
		document.documentElement.setAttribute('lang', code.split('-')[0]);
		isOpen = false;
		// Reload the page to fetch content in the new locale
		window.location.reload();
	}
	
	function handleKeyDown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			isOpen = false;
		}
	}
	
	// Close dropdown when clicking outside
	onMount(() => {
		function handleClickOutside(event: MouseEvent) {
			const target = event.target as HTMLElement;
			if (!target.closest('.language-switcher')) {
				isOpen = false;
			}
		}
		
		document.addEventListener('click', handleClickOutside);
		
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	});
</script>

<div class="language-switcher">
	<button
		class="language-button"
		onclick={toggleDropdown}
		onkeydown={handleKeyDown}
		aria-expanded={isOpen}
		aria-haspopup="listbox"
		aria-label={$_('components.languageSwitcher.selectLanguage')}
		title={currentLanguage.name}
	>
		<span class="language-flag">{currentLanguage.flag}</span>
		<svg 
			class="chevron {isOpen ? 'chevron--open' : ''}" 
			viewBox="0 0 20 20" 
			fill="currentColor"
		>
			<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
		</svg>
	</button>
	
	{#if isOpen}
		<!-- svelte-ignore a11y_no_static_element_interactions -->
		<!-- svelte-ignore a11y_click_events_have_key_events -->
		<div class="language-dropdown" role="listbox" tabindex="-1" onclick={(e) => e.stopPropagation()} onkeydown={(e) => e.key === 'Escape' && (isOpen = false)}>
			{#each languages as language}
				<button
					class="language-option {language.code === currentLocale ? 'language-option--active' : ''}"
					onclick={() => selectLanguage(language.code)}
					role="option"
					aria-selected={language.code === currentLocale}
				>
					<span class="language-flag">{language.flag}</span>
					<span class="language-name">{language.name}</span>
					{#if language.code === currentLocale}
						<svg class="checkmark" viewBox="0 0 20 20" fill="currentColor">
							<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
						</svg>
					{/if}
				</button>
			{/each}
		</div>
	{/if}
</div>

<style>
	.language-switcher {
		position: relative;
		font-size: var(--font-size-sm);
	}
	
	.language-button {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 2px;
		min-width: 40px;
		height: 40px;
		padding: 0 8px;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-md);
		color: var(--text-primary);
		cursor: pointer;
		transition: var(--transition-colors);
	}
	
	.language-button:hover {
		background: var(--bg-tertiary);
		transform: translateY(-1px);
	}
	
	.language-button:focus-visible {
		outline: 2px solid var(--brand-primary);
		outline-offset: 2px;
	}
	
	.language-flag {
		font-size: 1.1em;
		line-height: 1;
	}
	
	.chevron {
		width: 12px;
		height: 12px;
		transition: transform 0.2s ease;
		color: var(--text-tertiary);
	}
	
	.chevron--open {
		transform: rotate(180deg);
	}
	
	.language-dropdown {
		position: absolute;
		top: calc(100% + var(--space-1));
		left: 0;
		min-width: 180px;
		max-height: 400px;
		overflow-y: auto;
		background: var(--bg-primary);
		border: 1px solid var(--border-default);
		border-radius: var(--radius-md);
		box-shadow: var(--shadow-lg);
		z-index: var(--z-dropdown);
		animation: slideDown 0.2s ease-out;
	}
	
	.language-option {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		width: 100%;
		padding: var(--space-2) var(--space-3);
		background: transparent;
		border: none;
		color: var(--text-primary);
		font-size: inherit;
		font-weight: var(--font-weight-medium);
		text-align: left;
		cursor: pointer;
		transition: var(--transition-colors);
		position: relative;
	}
	
	.language-option:hover {
		background: var(--hover-overlay);
	}
	
	.language-option--active {
		background: hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.1);
		color: var(--text-brand);
	}
	
	.language-option .language-name {
		flex: 1;
	}
	
	.checkmark {
		width: 16px;
		height: 16px;
		color: var(--text-brand);
	}
	
	@keyframes slideDown {
		from {
			opacity: 0;
			transform: translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
	
	.language-option .language-name {
		flex: 1;
	}
</style>